import { useState, useRef, useEffect } from "react";
import { DatePicker } from "@/components/form/DatePicker";
import { PasswordInput } from "@/components/form/PasswordInput";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

const labelCls = "mb-1.5 block text-xs font-semibold text-slate-600";
const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-colors";

export function TextField({ label, value, onChange, testid, required, type = "text", placeholder, labelClassName, ...rest }) {
  return (
    <div>
      {label && (
        <label className={cn(labelCls, labelClassName)}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {type === "password" ? (
        <PasswordInput data-testid={testid} className={inputCls} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
      ) : (
      <input
        type={type}
        data-testid={testid}
        className={inputCls}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        {...rest}
      />
      )}
    </div>
  );
}

const money2 = (v) => (v === "" || v == null || isNaN(Number(v)) ? "" : Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export function CurrencyField({ label, value, onChange, testid, placeholder = "0.00", labelClassName }) {
  const [focus, setFocus] = useState(false);
  const [raw, setRaw] = useState("");
  return (
    <div>
      {label && <label className={cn(labelCls, labelClassName)}>{label}</label>}
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">$</span>
        <input type="text" inputMode="decimal" data-testid={testid} className={cn(inputCls, "pl-6")} placeholder={placeholder}
          value={focus ? raw : money2(value)}
          onFocus={() => { setFocus(true); setRaw(value === "" || value == null ? "" : String(value)); }}
          onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"); setRaw(v); onChange(v === "" ? "" : v); }}
          onBlur={() => { setFocus(false); if (raw !== "" && !isNaN(Number(raw))) onChange(String(Math.round(Number(raw) * 100) / 100)); }} />
      </div>
    </div>
  );
}

export function TextArea({ label, value, onChange, testid, rows = 3, placeholder }) {
  return (
    <div>
      {label && <label className={labelCls}>{label}</label>}
      <textarea
        data-testid={testid}
        rows={rows}
        className={cn(inputCls, "resize-y")}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, testid, required, placeholder = "Select…", allowEmpty = true }) {
  const opts = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <div>
      {label && (
        <label className={labelCls}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <select
        data-testid={testid}
        className={cn(inputCls, "appearance-none bg-[length:16px] bg-[right_0.6rem_center] bg-no-repeat pr-9")}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='none' stroke='%2364748b' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        }}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
      >
        {allowEmpty && <option value="">{placeholder}</option>}
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function DateField({ label, value, onChange, testid, inline, monthHint }) {
  if (inline) {
    return (
      <DatePicker label={label} value={value} onChange={onChange} testid={testid} className="py-1.5" monthHint={monthHint} />
    );
  }
  return (
    <div>
      {label && <label className={labelCls}>{label}</label>}
      <DatePicker value={value} onChange={onChange} testid={testid} monthHint={monthHint} />
    </div>
  );
}

export function FilterMultiSelect({ label, value = [], onChange, options, testid, placeholder = "All", className, selectAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const opts = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const selected = opts.filter((o) => value.includes(o.value));
  const toggle = (v) => (value.includes(v) ? onChange(value.filter((x) => x !== v)) : onChange([...value, v]));
  const summary = selected.length === 0 || (selectAll && selected.length === opts.length) ? placeholder : selected.length === 1 ? selected[0].label : `${placeholder} · ${selected.length}`;
  return (
    <div ref={ref} className={cn("relative", className)}>
      {label && <label className={labelCls}>{label}</label>}
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen((o) => !o)}
        className={cn(inputCls, "flex items-center justify-between gap-2 pr-3 text-left", selected.length ? "text-slate-900" : "text-slate-400")}
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full min-w-[11rem] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg" data-testid={testid ? `${testid}-menu` : undefined}>
          {opts.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">No options</div>}
          {selectAll && opts.length > 0 && (
            <button type="button" data-testid={testid ? `${testid}-select-all` : undefined}
              onClick={() => onChange(selected.length === opts.length ? [] : opts.map((o) => o.value))}
              className="w-full border-b border-slate-100 px-3 py-2 text-left text-sm font-semibold text-sky-700 hover:bg-sky-50">
              {selected.length === opts.length ? "Clear all" : "Select all"}
            </button>
          )}
          {opts.map((o) => {
            const on = value.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                onClick={() => toggle(o.value)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-sky-50"
              >
                <span className={cn("flex h-4 w-4 shrink-0 items-center justify-center rounded border", on ? "border-sky-500 bg-sky-500 text-white" : "border-slate-300 text-transparent")}>
                  <Check className="h-3 w-3" />
                </span>
                <span className="truncate">{o.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ClearFiltersButton({ active, onClick, testid = "clear-filters", className }) {
  if (!active) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn("inline-flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-2 text-sm font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800", className)}
    >
      <X className="h-3.5 w-3.5" /> Clear filters
    </button>
  );
}
